import axios from "axios";
import { normalizeHttpError } from "./httpError";
import GetUrlApi from "./UrlApi";
import { dispatch, store } from "src/redux/store";
import { addErrors, addRequests } from "src/redux/slices/errors";
import { addLatencySamples, addFailureSample } from "src/redux/slices/networkStatus";
import { createGetDeduper, uuid, isMutatingMethod } from "./requestDedupe";
import { showSnackbar } from "./globalSnackbar";
import { requestSupervisorOverride, getOverrideMutex } from "./permissionOverride";
import { getCurrentLease, notifyLeaseHeld } from "./mesaLeaseRegistry";
import { attachLeaseHeader } from "./mesaLeaseCore";
import { defaultTimeoutFor, createLatencySampler, cloneForMonitor } from "./httpPolicy";
import { markLocalCheckoutFetch, mutationTouchesMesa } from "./localCheckoutFetchTracker";
import { attachDebugHeaders, settleRequest } from "./debug/debugTrace";
import { record as recordDebug } from "./debug/debugRecorder";
import { rotatedEmployeeSessionRetryConfig } from "./employeeSessionRetry";
import { readStoredSessionIdentifier } from "./deviceSessionCore";
import { sessionOpInFlight } from "./employeeSessionOps";
import {
  isQuietEnd,
  isRetiredSessionError,
  notifyEmployeeSessionEnded,
  quietRetryConfig,
  retiredSessionRetryConfig,
} from "./employeeSessionEvents";
import { BUILD_ID } from "./buildInfo";
import { isRestaurantAuthFailure } from "./restaurantSession";

// -----------------------------------------------------------------------
// El interceptor SOLO guarda requests/errors en el slice cuando el switch
// global de monitorización está ON. La fuente de verdad es `state.errors.
// isMonitoring` que toca el `MonitoringMasterSwitchCard`. El flag legacy
// localStorage queda como fallback por compatibilidad.
const MONITOR_KEY = "APIMONITOR_ENABLED";
const isMonitoringEnabled = () => {
  try {
    if (store.getState()?.errors?.isMonitoring === true) return true;
  } catch { /* store no inicializado todavía */ }
  return window.localStorage.getItem(MONITOR_KEY) === "1";
};

// URLs del propio sistema de monitorización. Estas NO entran al slice
// para evitar el bucle: si el batch del monitor entra al slice, el
// siguiente flush lo manda → genera otro POST → otro item → infinito.
// `api-monitor/*` cubre el batch + queries del panel.
// `connectivity/sample|ping|event|history|...` cubre los del calidad/caídas.
const isMonitorOwnUrl = (url) => {
  if (!url) return false;
  return /(^|\/)api-monitor(\/|$)/.test(url) || /(^|\/)connectivity(\/|$)/.test(url);
};

// Fase 5 · Datos de la acción para la fila HTTP del monitor (sin la
// referencia viva a la acción: el slice solo guarda valores planos).
const debugInfoOf = (config) => {
  const d = config?.metadata?.debug;
  if (!d) return null;
  return { actionId: d.actionId || null, actionName: d.actionName || null, screen: d.screen || null };
};

// Clon para el monitor con tope de tamaño (Fase 3): antes se clonaba
// entera cada respuesta (bootstrap, catálogo…) con el monitor encendido.
const tryParse = (v) => cloneForMonitor(v);

// Fase 3 · Latencia agrupada: un único dispatch cada 2 s como mucho (antes
// uno por respuesta, que re-ejecutaba todos los selectores montados).
const latencySampler = createLatencySampler((batch) => {
  dispatch(addLatencySamples(batch));
});

// 2026-08-24 · Auto-refresh silencioso del X-Employee-Session cuando
// quedan <2h de vida. Lo dispara el interceptor request de axios en
// cualquier petición. Debounce en memoria con `_refreshInFlight` para
// no lanzar N refresh en paralelo cuando llegan varias requests a la
// vez. Fire-and-forget: no bloquea la request actual — el próximo
// tick tendrá el token nuevo.
const REFRESH_THRESHOLD_MS = 2 * 60 * 60 * 1000; // 2h
let _refreshInFlight = false;
let _lastRefreshAttemptMs = 0;
function scheduleSessionRefreshIfNeeded(expiresAtIso) {
  try {
    const expiresMs = new Date(expiresAtIso).getTime();
    if (!expiresMs || Number.isNaN(expiresMs)) return;
    const remainingMs = expiresMs - Date.now();
    if (remainingMs > REFRESH_THRESHOLD_MS) return;
    if (remainingMs <= 0) return; // caducado: el header no viaja, no hay nada que refrescar
    if (_refreshInFlight) return;
    // Retry cooldown: si el refresh anterior falló hace <30s, no
    // martillar el backend con reintentos en cada petición.
    if (Date.now() - _lastRefreshAttemptMs < 30_000) return;

    _refreshInFlight = true;
    _lastRefreshAttemptMs = Date.now();
    // Import diferido — evita ciclo: axios.js → slice → axios.js.
    // El slice hace `dispatch(setSession(res.data))` que persiste
    // el token nuevo en localStorage y actualiza Redux.
    import("src/redux/slices/employeeSession")
      .then((mod) => dispatch(mod.refreshEmployeeSession()))
      .catch(() => { /* no-op */ })
      .finally(() => { _refreshInFlight = false; });
  } catch { /* nunca romper el request por el auto-refresh */ }
}

// -----------------------------------------------------------------------
const axiosInstance = axios.create({
  baseURL: `${GetUrlApi()}/api`,
});

// Fase 3 · Fuera `X-Requested-With` y `Access-Control-Allow-Origin` (este
// último es una cabecera de RESPUESTA; en la petición no sirve de nada y,
// como cualquier cabecera no estándar, solo añadía peso a los preflight).
axiosInstance.defaults.headers.common = {
  "Content-Type": "application/json",
  // Rehidratado al cargar el módulo. La clave se guarda como
  // "sessionIdentifier" (minúscula) por `authJwt.setSession` — sin la
  // fallback antigua, tras un F5 el header viajaba vacío y flujos como
  // `SetCurrentDeviceAsClockingDevice` fallaban con "sin identificador".
  // 2026-10-26 · Nunca vacío: "" / "null" (restos de un login sin
  // identificador) se tratan como «sin identificador» y la cabecera no se
  // pone; `deviceSession.ensureDeviceSessionIdentifier` lo recupera.
  ...(readStoredSessionIdentifier()
    ? { SessionIdentifier: readStoredSessionIdentifier() }
    : {}),
  UserEmployeeId: Number(window.localStorage.getItem("UserEmployeeId")),
  // 2026-10-05 · Admin › Versiones: el API guarda qué build usa cada
  // dispositivo (Devices.ClientVersion). CORS del API: AllowAnyHeader.
  "X-Client-Version": BUILD_ID,
};

// Bearer rehidratado al cargar el módulo. Sin esto, las llamadas que dispara
// el DashboardLayout en su useEffect inicial (fetchLegalStatus, fetchWorkerStatus,
// etc.) salen ANTES de que el JwtProvider ejecute `setSession` y reciben 401.
// `setSession` sigue siendo la fuente de verdad cuando el usuario hace login
// limpio o cierra sesión — esto solo cubre el "reload con sesión válida".
const __cachedToken = window.localStorage.getItem("accessToken");
if (__cachedToken) {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${__cachedToken}`;
}

// Escape hatches per-request:
//   { skipDedupe: true }   → force a fresh request even if identical is in flight
//   { skipIdempotency: true } → do not attach Idempotency-Key
axiosInstance.interceptors.request.use(
  async (config) => {
    // metadata siempre para medir latencia real (independiente del toggle
    // de monitoring verbose). Cost: un Date.now() por request, despreciable.
    config.metadata = config.metadata || { startTime: new Date() };

    // Fase 3 · Timeout por defecto (GET 20 s, mutaciones 15 s) salvo que el
    // caller fije el suyo o sea una llamada larga (exportar, escanear…).
    // Sin él, una petición colgada con mala Wi-Fi bloqueaba al camarero
    // minutos. Un timeout no trae respuesta → `isUncertainOutcome` (Fase 0).
    const autoTimeout = defaultTimeoutFor(config);
    if (autoTimeout) config.timeout = autoTimeout;

    // Supervisor override mutex — si hay un override en curso (token del
    // supervisor temporalmente en localStorage), esperamos a que termine
    // antes de leer el token, para que las requests de otros callers no
    // acaben viajando con la identidad del supervisor. La reejecución del
    // propio override marca `__isOverrideRetry: true` y se salta la
    // espera para no autobloquearse.
    if (!config.__isOverrideRetry) {
      const mutex = getOverrideMutex();
      if (mutex) {
        try { await mutex; } catch { /* el override falló, seguimos */ }
      }
    }

    // F4 · Adjuntar `X-Employee-Session` firmado si hay PIN login activo.
    // El backend valida con `EmployeeSessionMiddleware` y aplica
    // `[RequireEmployeeAction]` cuando exista en el endpoint. Sin este
    // header sigue funcionando (retrocompat: el filtro pasa como admin
    // gracias a AllowRestaurantOwner=true).
    try {
      // 2026-10-18 · Token explícito (logout de una sesión ya soltada en
      // local): viaja ESE token y no el que haya ahora en localStorage —
      // si no, el logout podía cerrar la sesión recién emitida del
      // siguiente empleado.
      const explicitToken = config.__employeeSessionToken || null;
      const token = window.localStorage.getItem("EmployeeSessionToken");
      const expiresAt = window.localStorage.getItem("EmployeeSessionExpiresAt");
      if (explicitToken) {
        config.headers = config.headers || {};
        config.headers["X-Employee-Session"] = explicitToken;
      } else if (token && (!expiresAt || new Date(expiresAt) > new Date())) {
        config.headers = config.headers || {};
        config.headers["X-Employee-Session"] = token;

        // 2026-08-24 · Auto-renew silencioso si al token le quedan
        // <2h para caducar. El camarero puede pasar un servicio de 12+
        // horas sin volver a fichar. Fire-and-forget: no bloqueamos la
        // request actual; el nuevo token entra en localStorage antes de
        // la siguiente petición. Debounce con lock para no lanzar N
        // refreshes en paralelo si vienen múltiples requests.
        if (!config.__isSessionRefresh && expiresAt) {
          scheduleSessionRefreshIfNeeded(expiresAt);
        }
      }
    } catch { /* localStorage no disponible → seguir sin sesión */ }

    // Idempotency-Key for mutations so the backend can short-circuit duplicates.
    if (
      isMutatingMethod(config.method) &&
      !config.skipIdempotency &&
      !(
        config.headers &&
        (config.headers["Idempotency-Key"] || config.headers["idempotency-key"])
      )
    ) {
      config.headers = config.headers || {};
      config.headers["Idempotency-Key"] = uuid();
    }

    // Fase 1 TPV · lease de mesa: las mutaciones viajan con el lease de la
    // mesa en la que está este dispositivo; el backend responde 409
    // LEASE_HELD si otro dispositivo la ha tomado.
    attachLeaseHeader(config, getCurrentLease());
    // Recordamos la mesa del lease para marcar el eco de SignalR de esta
    // misma acción al volver la respuesta (ver interceptor de respuesta).
    // `config.leaseMesaId`: la petición es de OTRA mesa (auto-guardado de
    // la mesa que se acaba de dejar, con su propio lease en la cabecera).
    // Solo mutaciones que cambian la mesa: el heartbeat/lease, el diario o
    // la telemetría no traen eco y abrían la ventana que descarta eventos.
    const lease = getCurrentLease();
    const marksMesa = isMutatingMethod(config.method) && mutationTouchesMesa(config.url);
    if (config.leaseMesaId && marksMesa) {
      config.metadata.leaseMesaId = config.leaseMesaId;
    } else if (lease?.mesaId && marksMesa) {
      config.metadata.leaseMesaId = lease.mesaId;
    }

    // Fase 5 · Acción de usuario en curso → X-Action-Id / X-Action-Name /
    // X-Screen (solo hacia nuestra API) para el panel de depuración.
    try { attachDebugHeaders(config, axiosInstance.defaults.baseURL); } catch { /* nunca romper */ }

    return config;
  },
  (request) => request,
  (error) => error,
);

// Request dedupe de GETs idénticos en vuelo (doble tap, eco de SignalR
// que dispara el mismo refetch dos veces…). Antes se sobrescribía
// `axiosInstance.request`, pero en axios 1.x `get/post` están ligados al
// contexto interno y nunca pasaban por ese wrapper (código muerto), y si
// alguien llamaba a `request()` directamente deduplicaba también POSTs.
// Ahora: solo GET, y cada caller recibe su propia copia de `data`.
const originalGet = axiosInstance.get.bind(axiosInstance);
axiosInstance.get = createGetDeduper(originalGet);

// 401 EMPLOYEE_SESSION_EXPIRED definitivo: el servidor rechazó el token
// vigente. Solo entonces se borra la sesión local, se avisa al TPV y se
// muestra «sesión caducada». Si la petición llevaba un token que ya no es
// el guardado (hay uno más nuevo), NO se toca: borraría el bueno.
function clearDeadEmployeeSession(error) {
  try {
    const sent =
      error?.config?.headers?.["X-Employee-Session"] ||
      error?.config?.headers?.["x-employee-session"] ||
      null;
    const current = window.localStorage.getItem("EmployeeSessionToken");
    if (sent && current && sent !== current) return;
    window.localStorage.removeItem("EmployeeSessionToken");
    window.localStorage.removeItem("EmployeeSessionInfo");
    window.localStorage.removeItem("EmployeeSessionExpiresAt");
  } catch { /* ignore */ }
  // 2026-10-06 · Si el servidor dice por qué (el empleado entró en otro
  // dispositivo, un responsable cerró la sesión, baja), no es un error: el
  // TPV vuelve al kiosko con un aviso informativo y SIN la alerta de
  // «sesión caducada» (antes salía una por cada petición en vuelo).
  const body = error?.response?.data || {};
  if (isQuietEnd(body.reason)) {
    notifyEmployeeSessionEnded(Date.now(), {
      reason: body.reason,
      employeeName: body.employeeName || null,
      deviceName: body.deviceName || null,
    });
    return;
  }
  // El TPV deja de mostrar al empleado y vuelve al selector.
  if (notifyEmployeeSessionEnded()) {
    showSnackbar(
      "Tu sesión ha caducado. Vuelve a identificarte con PIN.",
      { variant: "warning", key: "auth-expired", preventDuplicate: true },
    );
  }
}

function retryAsDeviceAfterQuietEnd(error) {
  const cfg = quietRetryConfig(error);
  return cfg ? axiosInstance(cfg) : null;
}

// Reintento con el token de empleado vigente si la petición viajó con uno
// ya rotado (ver employeeSessionRetry.js). Null = no aplica.
function retryWithRotatedEmployeeSession(error) {
  const cfg = rotatedEmployeeSessionRetryConfig(error);
  return cfg ? axiosInstance(cfg) : null;
}

axiosInstance.interceptors.response.use(
  (response) => {
    // Latencia real del request — siempre, para alimentar el slice
    // networkStatus que a su vez pinta el indicador de red del TopBar.
    const reqMeta = response.config?.metadata;
    settleRequest(reqMeta?.debug?.action);
    if (reqMeta?.startTime) {
      latencySampler.add(Date.now() - new Date(reqMeta.startTime).getTime());
    }
    // Fase 3 · Una mutación de ESTE dispositivo sobre su mesa provoca un
    // `mesa/{rid}` de vuelta: lo marcamos como eco para no re-pedir el
    // checkout que la propia respuesta ya ha dejado al día.
    if (reqMeta?.leaseMesaId) markLocalCheckoutFetch(reqMeta.leaseMesaId);

    // Si está desactivado, no registrar
    if (!isMonitoringEnabled()) return response;
    // No registrar las llamadas del propio sistema de monitorización
    // — el batch del API monitor o el sample/ping de calidad. Si las
    // metemos al slice, el siguiente flush las re-envía → eco infinito.
    if (isMonitorOwnUrl(response.config?.url)) return response;

    const newRes = {};
    const meta = response.config.metadata;
    if (meta) {
      meta.endTime = new Date();
      newRes.duration = meta.endTime - meta.startTime;
      newRes.startedAt = meta.startTime;
      newRes.endedAt = meta.endTime;
    }

    newRes.method = response.config.method?.toUpperCase();
    newRes.url = response.config.url;
    newRes.baseURL = `${response.config.baseURL}/${response.config.url}`;
    newRes.queryParams = response.config.params;
    newRes.requestHeaders = response.config.headers;
    newRes.requestBody = tryParse(response.config.data || {});

    newRes.status = response.status;
    newRes.statusText = response.statusText;
    newRes.responseHeaders = response.headers;
    // CRÍTICO: deep clone con tryParse (no asignar la referencia). Si pasamos
    // response.data por referencia, immer del slice errors lo congela al
    // procesar `addRequests`, y cualquier reducer posterior que mute esa
    // misma referencia (ej: syncCartCheckoutComplete con response.data.checkout)
    // peta con "Cannot assign to read only property". El bug aparece SOLO
    // con el monitor ON porque OFF salta este interceptor.
    newRes.responseBody = tryParse(response.data);
    newRes.traceId = response.headers["x-trace-id"];
    newRes.debug = debugInfoOf(response.config);

    dispatch(addRequests(newRes));
    return response;
  },
  (error) => {
    // Incrementar contador de fallos consecutivos para el indicador de red —
    // picos rápidos sugieren red caída aunque navigator.onLine siga en true.
    try { dispatch(addFailureSample()); } catch { /* ignore */ }
    settleRequest(error?.config?.metadata?.debug?.action);

    // Sistema de permisos F-Empleados. El backend devuelve shapes
    // conocidos { code, message, action, roleName? }:
    //   • 403 PERMISSION_DENIED — el empleado no tiene la acción.
    //     → SUPERVISOR OVERRIDE: abrimos PinLoginDialog para que un
    //       supervisor autorice y la request se reejecuta con SU token.
    //       Tras terminar, restauramos la sesión del empleado original.
    //   • 401 AUTH_EMPLOYEE_REQUIRED — no hay sesión de empleado.
    //   • 401 AUTH_LEGACY_HEADER_BLOCKED — token legacy rechazado.
    try {
      const status = error?.response?.status;
      const body = error?.response?.data;
      const code = body?.code;
      // `skipLeaseHeldNotify`: petición de otra mesa (auto-guardado al salir)
      // → un 409 suyo no significa que me hayan quitado la mesa ACTUAL.
      if (status === 409 && code === "LEASE_HELD" && !error?.config?.skipLeaseHeldNotify) {
        notifyLeaseHeld({ holder: body?.holder || null, message: body?.message });
      }
      // Fase 5 · Conflictos que el cliente bloquea/gestiona (lease, cobro ya
      // hecho, clave reutilizada…) → evento "dup" para el panel.
      if (status === 409) {
        const d = error?.config?.metadata?.debug;
        recordDebug("dup", {
          // Sin acción en la petición → hereda la acción en curso (el panel
          // cruza duplicados y clicks por actionId).
          actionId: d?.actionId || undefined,
          actionName: d?.actionName || undefined,
          screen: d?.screen,
          eventType: code || body?.errorCode || "CONFLICT_409",
          target: `${(error?.config?.method || "").toUpperCase()} ${error?.config?.url || ""}`,
          detail: { holder: body?.holder || null, message: body?.message || null },
        });
      }
      if (status === 403 && code === "PERMISSION_DENIED") {
        // Devolvemos la promesa del override — si el supervisor
        // autoriza, resuelve con la respuesta de la re-ejecución;
        // si cancela, rechaza con el 403 original.
        return requestSupervisorOverride(error, axiosInstance);
      }
      if (status === 401 && (code === "AUTH_EMPLOYEE_REQUIRED" || code === "AUTH_LEGACY_HEADER_BLOCKED")) {
        // 401 sin sesión de empleado → snackbar directo para que el
        // usuario sepa que tiene que identificarse. No abrimos PIN
        // dialog automáticamente aquí porque puede que el flujo
        // esperado sea que el admin dueño se logue y no un empleado.
        showSnackbar(
          "Identifícate con PIN para continuar.",
          {
            variant: "warning",
            key: `auth-${code}`,
            preventDuplicate: true,
          },
        );
      }
      if (status === 401 && code === "EMPLOYEE_SESSION_EXPIRED" && isRetiredSessionError(error)) {
        // 2026-10-18 · El token lo soltó este dispositivo al cambiar de
        // empleado (p. ej. empleado → admin con PIN). No es la sesión
        // actual la que ha caducado: NO se vuelve al kiosko (eso pedía el
        // PIN otra vez). Las consultas se repiten con la identidad vigente.
        const retry = retiredSessionRetryConfig(error);
        if (retry) return axiosInstance(retry);
        return Promise.reject(normalizeHttpError(error));
      }
      if (status === 401 && code === "EMPLOYEE_SESSION_EXPIRED") {
        // Hay una renovación/emisión de la sesión en curso: esta respuesta
        // puede ser del token anterior. Se espera a que termine y se
        // reintenta con el vigente; solo si sigue siendo el mismo token
        // la sesión está muerta de verdad.
        const pending = sessionOpInFlight();
        if (pending && !error.config?.__employeeSessionRetried) {
          return pending.then(() => {
            const again = retryWithRotatedEmployeeSession(error);
            if (again) return again;
            clearDeadEmployeeSession(error);
            const quietRetry = retryAsDeviceAfterQuietEnd(error);
            if (quietRetry) return quietRetry;
            return Promise.reject(normalizeHttpError(error));
          });
        }
        // Token rotado mientras la petición viajaba (p. ej. la renovación
        // silenciosa al recargar revoca el anterior): no es una sesión
        // muerta. Se reintenta una vez con el token vigente en vez de
        // borrarlo (antes se borraba el token NUEVO y el empleado se perdía).
        const retry = retryWithRotatedEmployeeSession(error);
        if (retry) return retry;
        clearDeadEmployeeSession(error);
        // 2026-10-06 · Sesión cortada «a propósito» (el empleado entró en otro
        // dispositivo, la cerró un responsable, baja): las CONSULTAS se repiten
        // una vez como el propio dispositivo (sin el empleado), que es lo que
        // es mientras está en el kiosko. Sin esto, al arrancar con el token
        // revocado fallaba hasta `authentication/user` y se cerraba la sesión
        // del RESTAURANTE. Las escrituras no se repiten (necesitan empleado).
        const quietRetry = retryAsDeviceAfterQuietEnd(error);
        if (quietRetry) return quietRetry;
      }
      if (status === 401 && isRestaurantAuthFailure(error)) {
        // 2026-10-28 · 401 del token del RESTAURANTE (no de la sesión PIN):
        // un intento de renovación y se repite la petición; si el servidor la
        // rechaza → hubs parados, sesión cerrada y UN aviso (ver
        // restaurantSessionRuntime). Antes cada petición y cada hub repetían
        // el 401 sin fin sin decir nada.
        return import("./restaurantSessionRuntime")
          .then((m) => m.resolveRestaurantUnauthorized(error))
          .catch(() => null)
          .then((retry) => (retry ? axiosInstance(retry) : Promise.reject(normalizeHttpError(error))));
      }
    } catch { /* snackbar nunca rompe la request */ }

    // Si está desactivado, no registrar
    if (!isMonitoringEnabled()) {
      return Promise.reject(normalizeHttpError(error));
    }
    // Tampoco las URLs propias del monitor (anti-loop).
    if (isMonitorOwnUrl(error.config?.url)) {
      return Promise.reject(normalizeHttpError(error));
    }

    const newError = {};
    const meta = error.config?.metadata;
    if (meta) {
      meta.endTime = new Date();
      newError.duration = meta.endTime - meta.startTime;
      newError.startedAt = meta.startTime;
      newError.endedAt = meta.endTime;
    } else {
      newError.endedAt = new Date();
    }

    newError.method = error.config?.method?.toUpperCase();
    newError.url = error.config?.url;
    newError.baseURL = `${error.config?.baseURL}/${error.config?.url}`;
    newError.queryParams = error.config?.params;
    newError.requestHeaders = error.config?.headers;
    newError.requestBody = tryParse(error?.config?.data || {});

    if (error.response) {
      newError.status = error.response.status;
      newError.statusText = error.response.statusText;
      newError.responseHeaders = error.response.headers;
      // Deep clone, mismo motivo que en la rama de éxito.
      newError.responseBody = tryParse(error.response.data);
      newError.traceId = error.response?.headers["x-trace-id"];
      newError.customMessage = tryParse(error.response?.data);
    }
    newError.debug = debugInfoOf(error.config);

    newError.message = error.message;
    newError.code = error.code;

    dispatch(addErrors(newError));
    return Promise.reject(normalizeHttpError(error));
  },
);

export default axiosInstance;
